import React, { useState, useEffect } from "react";
import {
  Users,
  Clock,
  BarChart,
  Briefcase,
  DollarSign,
  Send,
  Heart,
  Settings,
  Globe,
  Shield,
  Activity,
  ChevronRight,
  Search,
  X,
  Eye,
} from "lucide-react";
import { theme } from "../../styles/theme";
import scenarioApi from "../../api/scenarioApi";
import administrationApi, { Country } from "../../api/administrationApi";
import lensesApi, { StakeholderLens } from "../../api/lensesApi";
import metricsApi, { Cluster } from "../../api/metricsApi";
import dashboardApi from "../../api/dashboardApi";
import TemporalDistributionChart from "./TemporalDistributionChart";
import NonAggregatableMetrics from "./Widgets/NonAggregatableMetrics";

interface ImpactDashboardProps {
  darkMode: boolean;
}

interface DashboardData {
  totalEconomicValue: number;
  economicGrowth: number;
  peopleImpacted: number;
  coveragePercentage: number;
  avgTimeSaved: number;
  totalMetrics: number;
  clusters: ClusterData[];
}

interface ClusterData {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  metrics: number;
  economicValue: string;
  time?: string;
  people: string;
  progress: number;
  color: string;
  rawEconomicValue?: number;
  rawPeopleImpacted?: number;
}

interface MetricDetail {
  id: string;
  name: string;
  definition: string;
  value: number;
  unit: string;
  type: string;
}

const getClusterIcon = (clusterCode: string): React.ComponentType<any> => {
  const iconMap: { [key: string]: React.ComponentType<any> } = {
    C1: DollarSign,
    C2: Briefcase,
    C3: Send,
    C4: Heart,
    C5: Settings,
    C6: Globe,
    C7: Shield,
  };
  return iconMap[clusterCode] || Activity;
};

const getClusterColor = (clusterCode: string): string => {
  const colorMap: { [key: string]: string } = {
    C1: "#DC3545",
    C2: "#007BFF",
    C3: "#28A745",
    C4: "#6F42C1",
    C5: "#FD7E14",
    C6: "#17A2B8",
    C7: "#E83E8C",
  };
  return colorMap[clusterCode] || theme.colors.primary.main;
};

// Country Selector Component
interface CountrySelectorProps {
  countries: Country[];
  selectedCountry: number | null;
  onSelectCountry: (countryId: number | null) => void;
  darkMode: boolean;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
  countries,
  selectedCountry,
  onSelectCountry,
  darkMode,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredCountry, setHoveredCountry] = useState<number | null>(null);

  const filteredCountries = countries.filter((country) =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedCountryData = countries.find((c) => c.id === selectedCountry);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".country-selector")) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="country-selector relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 rounded-lg flex items-center justify-between"
        style={{
          backgroundColor: darkMode
            ? theme.colors.background.dark
            : theme.colors.background.default,
          border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
          color: darkMode
            ? theme.colors.text.darkPrimary
            : theme.colors.text.primary,
        }}
      >
        <div className="flex items-center space-x-2">
          {selectedCountryData ? (
            <>
              <span className="text-xl">{selectedCountryData.flag_emoji}</span>
              <span>{selectedCountryData.name}</span>
            </>
          ) : (
            <span style={{ color: theme.colors.text.secondary }}>
              Select a country...
            </span>
          )}
        </div>
        <ChevronRight
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-90" : ""}`}
          style={{ color: theme.colors.text.secondary }}
        />
      </button>

      {isOpen && (
        <div
          className="absolute z-50 w-full rounded-lg shadow-xl overflow-hidden mt-2"
          style={{
            backgroundColor: darkMode
              ? theme.colors.background.darkPaper
              : theme.colors.background.paper,
            border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
            maxWidth: "300px",
            minWidth: "250px",
          }}
        >
          <div className="p-3 border-b" style={{
            borderColor: darkMode ? theme.colors.border.dark : theme.colors.border.light,
          }}>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                style={{ color: theme.colors.text.secondary }}
              />
              <input
                type="text"
                placeholder="Search countries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2 rounded-lg"
                style={{
                  backgroundColor: darkMode
                    ? theme.colors.background.dark
                    : theme.colors.background.default,
                  border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                  color: darkMode
                    ? theme.colors.text.darkPrimary
                    : theme.colors.text.primary,
                }}
                autoFocus
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  <X className="w-4 h-4" style={{ color: theme.colors.text.secondary }} />
                </button>
              )}
            </div>
          </div>

          <div
            className="overflow-y-auto"
            style={{ maxHeight: "300px" }}
          >
            {filteredCountries.length === 0 ? (
              <div className="p-4 text-center" style={{ color: theme.colors.text.secondary }}>
                No countries found
              </div>
            ) : (
              <div className="p-2">
                {filteredCountries.map((country) => (
                  <button
                    key={country.id}
                    onClick={() => {
                      onSelectCountry(country.id);
                      setIsOpen(false);
                      setSearchTerm("");
                    }}
                    onMouseEnter={() => setHoveredCountry(country.id)}
                    onMouseLeave={() => setHoveredCountry(null)}
                    className="w-full px-3 py-2 rounded-lg flex items-center space-x-3 transition-colors mb-1"
                    style={{
                      backgroundColor:
                        selectedCountry === country.id
                          ? theme.colors.primary.main + "20"
                          : hoveredCountry === country.id
                            ? darkMode
                              ? theme.colors.background.dark
                              : theme.colors.background.default
                            : "transparent",
                      color:
                        selectedCountry === country.id
                          ? theme.colors.primary.main
                          : darkMode
                            ? theme.colors.text.darkPrimary
                            : theme.colors.text.primary,
                    }}
                  >
                    <span className="text-xl">{country.flag_emoji}</span>
                    <div className="flex-1 text-left">
                      <div className="font-medium">{country.name}</div>
                      <div
                        className="text-xs"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {country.iso_code} • {country.region}
                      </div>
                    </div>
                    {selectedCountry === country.id && (
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: theme.colors.primary.main }}
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ImpactDashboard: React.FC<ImpactDashboardProps> = ({ darkMode }) => {
  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);
  const [selectedLens, setSelectedLens] = useState<StakeholderLens | null>(null);
  const [selectedScenario, setSelectedScenario] = useState<"pessimistic" | "baseline" | "optimistic">("baseline");
  const [countries, setCountries] = useState<Country[]>([]);
  const [lenses, setLenses] = useState<StakeholderLens[]>([]);
  const [, setScenarioDefinitions] = useState<any[]>([]);
  const [, setClusters] = useState<Cluster[]>([]);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedClusterForDetails, setSelectedClusterForDetails] = useState<ClusterData | null>(null);
  const [clusterMetrics, setClusterMetrics] = useState<MetricDetail[]>([]);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [selectedClusterForTemporal, setSelectedClusterForTemporal] = useState<string | null>(null);
  const [temporalData, setTemporalData] = useState<any | null>(null);
  const [loadingTemporal, setLoadingTemporal] = useState(false);

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Update dashboard when selections change
  useEffect(() => {
    if (selectedCountryId && selectedLens) {
      fetchDashboardData();
    }
  }, [selectedCountryId, selectedLens, selectedScenario]);

  const fetchInitialData = async () => {
    try {
      // Fetch countries
      const countriesResponse = await administrationApi.getCountries({ limit: 1000 });
      setCountries(countriesResponse.countries);
      
      // Fetch lenses
      const lensesResponse = await lensesApi.getLenses({ active_only: true });
      setLenses(lensesResponse);
      
      // Fetch scenario definitions
      const scenarios = await scenarioApi.getScenarioDefinitions(true);
      setScenarioDefinitions(scenarios);
      
      // Fetch clusters
      const clustersResponse = await metricsApi.getClusters();
      setClusters(clustersResponse);
      
      // Set defaults
      if (countriesResponse.countries.length > 0) {
        const defaultCountry = countriesResponse.countries.find(c => c.iso_code === "DE") || countriesResponse.countries[0];
        setSelectedCountryId(defaultCountry.id);
      }
      if (lensesResponse.length > 0) {
        setSelectedLens(lensesResponse[0]);
      }
    } catch (error) {
      console.error("Failed to fetch initial data:", error);
    }
  };

  const fetchDashboardData = async () => {
    if (!selectedCountryId || !selectedLens) {
      console.log("Skipping fetch - missing country or lens", { selectedCountryId, selectedLens });
      return;
    }
    
    console.log("Fetching dashboard data with:", { selectedCountryId, selectedLens, selectedScenario });
    setLoading(true);
    try {
      // Fetch real data from the backend
      const apiData = await dashboardApi.getDashboardData(
        selectedCountryId,
        selectedLens.id,
        selectedScenario
      );
      
      console.log("Received API data:", apiData);
      
      // Transform API data to component format
      const transformedData: DashboardData = {
        totalEconomicValue: apiData.total_economic_value, // Keep raw value
        economicGrowth: apiData.economic_growth,
        peopleImpacted: apiData.people_impacted,
        coveragePercentage: apiData.coverage_percentage,
        avgTimeSaved: apiData.avg_time_saved,
        totalMetrics: apiData.total_metrics,
        clusters: apiData.clusters
          .map(cluster => ({
            id: cluster.id,
            name: cluster.name,
            icon: getClusterIcon(cluster.id),
            metrics: cluster.metrics_count,
            economicValue: formatEconomicValue(cluster.economic_value),
            time: cluster.time_saved ? `-${cluster.time_saved}d` : undefined,
            people: formatPeople(cluster.people_impacted),
            progress: cluster.progress,
            color: getClusterColor(cluster.id),
            rawEconomicValue: cluster.economic_value,
            rawPeopleImpacted: cluster.people_impacted,
          }))
          .sort((a, b) => {
            // Sort by cluster ID (C1, C2, C3, etc.)
            const aNum = parseInt(a.id.replace('C', ''));
            const bNum = parseInt(b.id.replace('C', ''));
            return aNum - bNum;
          }),
      };
      
      console.log("Transformed data:", transformedData);
      setDashboardData(transformedData);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + "M";
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(2) + "K";
    }
    return num.toFixed(2);
  };

  const formatEconomicValue = (value: number): string => {
    const billion = 1000000000;
    const million = 1000000;
    const thousand = 1000;
    
    if (value >= billion) {
      return `$${(value / billion).toFixed(2)}B`;
    } else if (value >= million) {
      return `$${(value / million).toFixed(2)}M`;
    } else if (value >= thousand) {
      return `$${(value / thousand).toFixed(2)}K`;
    }
    return `$${value.toFixed(2)}`;
  };

  const formatPeople = (count: number): string => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(2)}M people`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(2)}K people`;
    }
    return `${count.toFixed(0)} people`;
  };

  const getScenarioLabel = (scenario: string): string => {
    switch (scenario) {
      case "pessimistic":
        return "Pessimistic";
      case "baseline":
        return "Baseline";
      case "optimistic":
        return "Optimistic";
      default:
        return "Baseline";
    }
  };

  const getScenarioColor = (scenario: string): string => {
    switch (scenario) {
      case "pessimistic":
        return "#DC3545";
      case "baseline":
        return "#007BFF";
      case "optimistic":
        return "#28A745";
      default:
        return theme.colors.primary.main;
    }
  };

  const handleShowClusterDetails = async (cluster: ClusterData) => {
    setSelectedClusterForDetails(cluster);
    setLoadingMetrics(true);
    
    try {
      // Fetch real metrics data from the new API endpoint
      if (selectedCountryId && selectedScenario) {
        const response = await dashboardApi.getClusterMetricsDetails(
          selectedCountryId,
          cluster.id,
          selectedScenario
        );
        setClusterMetrics(response.metrics || []);
      } else {
        setClusterMetrics([]);
      }
    } catch (error) {
      console.error("Failed to fetch cluster metrics:", error);
      setClusterMetrics([]);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedClusterForDetails(null);
    setClusterMetrics([]);
  };

  const handleClusterCardClick = async (clusterId: string, cluster: ClusterData) => {
    // If clicking the same cluster, close it
    if (selectedClusterForTemporal === clusterId) {
      setSelectedClusterForTemporal(null);
      setTemporalData(null);
      return;
    }

    setSelectedClusterForTemporal(clusterId);
    setLoadingTemporal(true);
    
    try {
      if (selectedCountryId) {
        const response = await dashboardApi.getTemporalDistribution(
          clusterId,
          selectedCountryId
        );
        console.log('API Response for temporal distribution:', response);
        setTemporalData({
          ...response,
          clusterName: cluster.name,
          baseEconomicValue: cluster.rawEconomicValue || 0,
          basePeopleImpacted: cluster.rawPeopleImpacted || 0,
        });
      }
    } catch (error) {
      console.error("Failed to fetch temporal distribution:", error);
      // Use default distribution on error
      setTemporalData({
        cluster_id: clusterId,
        clusterName: cluster.name,
        baseEconomicValue: cluster.rawEconomicValue || 0,
        basePeopleImpacted: cluster.rawPeopleImpacted || 0,
        yearly_percentages: [
          { year: 1, percentage: 10 },
          { year: 2, percentage: 15 },
          { year: 3, percentage: 20 },
          { year: 4, percentage: 20 },
          { year: 5, percentage: 20 },
          { year: 6, percentage: 15 },
        ],
        is_default: true,
      });
    } finally {
      setLoadingTemporal(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Selectors */}
      <div
        className="rounded-lg p-6"
        style={{
          backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
          borderRadius: theme.widget.borderRadius,
          boxShadow: theme.widget.boxShadow,
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2
            className="text-xl font-semibold"
            style={{
              color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
              fontFamily: theme.fonts.primary,
            }}
          >
            Impact Dashboard
          </h2>
          <div className="flex items-center space-x-4">
            {/* Country Selector */}
            <div style={{ minWidth: "250px" }}>
              <label
                className="text-sm block mb-1"
                style={{
                  color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
                }}
              >
                Country
              </label>
              <CountrySelector
                countries={countries}
                selectedCountry={selectedCountryId}
                onSelectCountry={setSelectedCountryId}
                darkMode={darkMode}
              />
            </div>

            {/* Lens Selector */}
            <div>
              <label
                className="text-sm block mb-1"
                style={{
                  color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
                }}
              >
                Lens
              </label>
              <select
                value={selectedLens?.id || ""}
                onChange={(e) => {
                  const lens = lenses.find(l => l.id === parseInt(e.target.value));
                  setSelectedLens(lens || null);
                }}
                className="px-3 py-2 rounded-lg border"
                style={{
                  backgroundColor: darkMode ? "#1a1a1a" : "white",
                  borderColor: darkMode ? theme.colors.border.dark : theme.colors.border.light,
                  color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                }}
              >
                {lenses.map((lens) => (
                  <option key={lens.id} value={lens.id}>
                    {lens.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Scenario Selector */}
            <div>
              <label
                className="text-sm block mb-1"
                style={{
                  color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
                }}
              >
                Scenario
              </label>
              <div className="flex space-x-2">
                {["pessimistic", "baseline", "optimistic"].map((scenario) => (
                  <button
                    key={scenario}
                    onClick={() => setSelectedScenario(scenario as any)}
                    className="px-3 py-2 rounded-lg transition-colors text-sm"
                    style={{
                      backgroundColor: selectedScenario === scenario
                        ? getScenarioColor(scenario)
                        : "transparent",
                      border: `1px solid ${getScenarioColor(scenario)}`,
                      color: selectedScenario === scenario
                        ? "white"
                        : getScenarioColor(scenario),
                    }}
                  >
                    {getScenarioLabel(scenario)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Impact Summary */}
        {dashboardData && (
          <div>
            <div className="mb-4">
              <h3
                className="text-lg font-medium mb-2"
                style={{
                  color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                  fontFamily: theme.fonts.primary,
                }}
              >
                Impact Summary
              </h3>
              <p
                className="text-sm"
                style={{
                  color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
                }}
              >
                With a <span style={{ color: getScenarioColor(selectedScenario), fontWeight: 600 }}>
                  {selectedScenario === "pessimistic" ? "5%" : selectedScenario === "optimistic" ? "15%" : "10%"} system improvement
                </span>, {countries.find(c => c.id === selectedCountryId)?.name || "Germany"} could generate{" "}
                <span style={{ color: theme.colors.status.success, fontWeight: 600 }}>
                  {formatEconomicValue(dashboardData.totalEconomicValue)}
                </span>{" "}
                in annual economic value while improving outcomes for{" "}
                <span style={{ color: theme.colors.secondary.main, fontWeight: 600 }}>
                  {formatNumber(dashboardData.peopleImpacted)}
                </span>{" "}
                migrants.
              </p>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-4 gap-4">
              <div
                className="p-4 rounded-lg"
                style={{
                  backgroundColor: darkMode ? "#1a1a1a" : "#f8f9fa",
                }}
              >
                <div className="flex items-center space-x-2 mb-2">
                  <DollarSign className="w-5 h-5" style={{ color: theme.colors.status.success }} />
                  <span
                    className="text-sm"
                    style={{ color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary }}
                  >
                    Total Economic Value
                  </span>
                </div>
                <div
                  className="text-2xl font-bold"
                  style={{ color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary }}
                >
                  {formatEconomicValue(dashboardData.totalEconomicValue)}
                </div>
                <div className="text-sm" style={{ color: theme.colors.status.success }}>
                  ↑ {dashboardData.economicGrowth.toFixed(2)}% vs last year
                </div>
              </div>

              <div
                className="p-4 rounded-lg"
                style={{
                  backgroundColor: darkMode ? "#1a1a1a" : "#f8f9fa",
                }}
              >
                <div className="flex items-center space-x-2 mb-2">
                  <Users className="w-5 h-5" style={{ color: theme.colors.secondary.main }} />
                  <span
                    className="text-sm"
                    style={{ color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary }}
                  >
                    People Impacted
                  </span>
                </div>
                <div
                  className="text-2xl font-bold"
                  style={{ color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary }}
                >
                  {formatNumber(dashboardData.peopleImpacted)}
                </div>
                <div className="text-sm" style={{ color: theme.colors.secondary.main }}>
                  ↑ {dashboardData.coveragePercentage.toFixed(2)}% coverage
                </div>
              </div>

              <div
                className="p-4 rounded-lg"
                style={{
                  backgroundColor: darkMode ? "#1a1a1a" : "#f8f9fa",
                }}
              >
                <div className="flex items-center space-x-2 mb-2">
                  <Clock className="w-5 h-5" style={{ color: "#6F42C1" }} />
                  <span
                    className="text-sm"
                    style={{ color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary }}
                  >
                    Avg. Time Saved
                  </span>
                </div>
                <div
                  className="text-2xl font-bold"
                  style={{ color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary }}
                >
                  {dashboardData.avgTimeSaved.toFixed(2)} days
                </div>
                <div className="text-sm" style={{ color: "#6F42C1" }}>
                  Per application
                </div>
              </div>

              <div
                className="p-4 rounded-lg"
                style={{
                  backgroundColor: darkMode ? "#1a1a1a" : "#f8f9fa",
                }}
              >
                <div className="flex items-center space-x-2 mb-2">
                  <BarChart className="w-5 h-5" style={{ color: "#FD7E14" }} />
                  <span
                    className="text-sm"
                    style={{ color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary }}
                  >
                    Total Metrics
                  </span>
                </div>
                <div
                  className="text-2xl font-bold"
                  style={{ color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary }}
                >
                  {dashboardData.totalMetrics}
                </div>
                <div className="text-sm" style={{ color: "#FD7E14" }}>
                  Tracked KPIs
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cluster Cards Grid */}
      {dashboardData && (
        <div className="grid grid-cols-3 gap-4">
          {dashboardData.clusters.map((cluster) => {
            const Icon = cluster.icon;
            return (
              <div
                key={cluster.id}
                className="p-5 rounded-lg transition-all hover:shadow-lg cursor-pointer"
                onClick={() => handleClusterCardClick(cluster.id, cluster)}
                style={{
                  backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
                  borderRadius: theme.widget.borderRadius,
                  boxShadow: theme.widget.boxShadow,
                  border: `2px solid ${selectedClusterForTemporal === cluster.id ? cluster.color : darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{
                        backgroundColor: `${cluster.color}20`,
                      }}
                    >
                      <Icon className="w-5 h-5" style={{ color: cluster.color }} />
                    </div>
                    <div>
                      <h4
                        className="font-semibold"
                        style={{
                          color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                          fontFamily: theme.fonts.primary,
                        }}
                      >
                        {cluster.name}
                      </h4>
                      <span
                        className="text-xs"
                        style={{
                          color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
                        }}
                      >
                        {cluster.metrics} metrics
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShowClusterDetails(cluster);
                    }}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="View details"
                  >
                    <Eye 
                      className="w-4 h-4" 
                      style={{ 
                        color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary 
                      }} 
                    />
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className="text-xs"
                        style={{
                          color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
                        }}
                      >
                        Economic Value
                      </span>
                      <span
                        className="text-sm font-semibold"
                        style={{
                          color: theme.colors.status.success,
                        }}
                      >
                        {cluster.economicValue}
                      </span>
                    </div>
                  </div>

                  {cluster.time && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className="text-xs"
                          style={{
                            color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
                          }}
                        >
                          Time
                        </span>
                        <span
                          className="text-sm font-semibold"
                          style={{
                            color: "#6F42C1",
                          }}
                        >
                          {cluster.time}
                        </span>
                      </div>
                    </div>
                  )}

                  <div>
                    <span
                      className="text-xs"
                      style={{
                        color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
                      }}
                    >
                      {cluster.people}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Temporal Distribution Chart */}
      {selectedClusterForTemporal && temporalData && !loadingTemporal && (
        <TemporalDistributionChart
          clusterName={temporalData.clusterName}
          clusterId={temporalData.cluster_id}
          baseEconomicValue={temporalData.baseEconomicValue}
          basePeopleImpacted={temporalData.basePeopleImpacted}
          yearlyPercentages={temporalData.yearly_percentages}
          darkMode={darkMode}
          onClose={() => {
            setSelectedClusterForTemporal(null);
            setTemporalData(null);
          }}
        />
      )}

      {/* Loading Temporal Chart */}
      {loadingTemporal && (
        <div
          className="mt-6 p-6 rounded-lg shadow-lg flex items-center justify-center"
          style={{
            backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
            border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
            height: "400px",
          }}
        >
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p style={{ color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary }}>
              Loading temporal distribution...
            </p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div
            className="text-lg"
            style={{
              color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
            }}
          >
            Loading dashboard data...
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !dashboardData && (
        <div
          className="text-center py-12 rounded-lg"
          style={{
            backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
            borderRadius: theme.widget.borderRadius,
          }}
        >
          <p
            style={{
              color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
            }}
          >
            Select a country and lens to view the impact dashboard
          </p>
        </div>
      )}

      {/* Non-Aggregatable Metrics Section */}
      {dashboardData && selectedCountryId && (
        <div className="mt-6">
          <NonAggregatableMetrics 
            darkMode={darkMode}
            countryId={selectedCountryId}
          />
        </div>
      )}

      {/* Cluster Details Modal */}
      {selectedClusterForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl max-h-[80vh] overflow-hidden shadow-xl"
            style={{
              backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
            }}
          >
            {/* Modal Header */}
            <div 
              className="flex items-center justify-between p-6 border-b"
              style={{
                borderColor: darkMode ? theme.colors.border.dark : theme.colors.border.light,
              }}
            >
              <div className="flex items-center space-x-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{
                    backgroundColor: `${selectedClusterForDetails.color}20`,
                  }}
                >
                  {React.createElement(selectedClusterForDetails.icon, {
                    className: "w-5 h-5",
                    style: { color: selectedClusterForDetails.color }
                  })}
                </div>
                <div>
                  <h3 
                    className="text-xl font-semibold"
                    style={{
                      color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                      fontFamily: theme.fonts.primary,
                    }}
                  >
                    {selectedClusterForDetails.name} - Metrics Breakdown
                  </h3>
                  <p
                    className="text-sm"
                    style={{
                      color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
                    }}
                  >
                    {selectedClusterForDetails.metrics} metrics contributing to impact
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5" style={{ color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary }} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-200px)]">
              {loadingMetrics ? (
                <div className="flex justify-center py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p style={{ color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary }}>
                      Loading metrics...
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div 
                      className="p-4 rounded-lg"
                      style={{
                        backgroundColor: darkMode ? "#1a1a1a" : "#f8f9fa",
                      }}
                    >
                      <p className="text-sm mb-1" style={{ color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary }}>
                        Total Economic Value
                      </p>
                      <p className="text-lg font-semibold" style={{ color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary }}>
                        {selectedClusterForDetails.economicValue}
                      </p>
                    </div>
                    <div 
                      className="p-4 rounded-lg"
                      style={{
                        backgroundColor: darkMode ? "#1a1a1a" : "#f8f9fa",
                      }}
                    >
                      <p className="text-sm mb-1" style={{ color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary }}>
                        People Impacted
                      </p>
                      <p className="text-lg font-semibold" style={{ color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary }}>
                        {selectedClusterForDetails.people}
                      </p>
                    </div>
                  </div>

                  {/* Metrics Table */}
                  <div>
                    <h4 
                      className="text-sm font-semibold mb-3"
                      style={{
                        color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                      }}
                    >
                      Individual Metrics
                    </h4>
                    <div 
                      className="border rounded-lg overflow-hidden"
                      style={{
                        borderColor: darkMode ? theme.colors.border.dark : theme.colors.border.light,
                      }}
                    >
                      <table className="w-full">
                        <thead>
                          <tr 
                            style={{
                              backgroundColor: darkMode ? "#1a1a1a" : "#f8f9fa",
                            }}
                          >
                            <th className="text-left p-3 text-sm font-medium" style={{ color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary }}>
                              Metric Name
                            </th>
                            <th className="text-right p-3 text-sm font-medium" style={{ color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary }}>
                              Value
                            </th>
                            <th className="text-center p-3 text-sm font-medium" style={{ color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary }}>
                              Type
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {clusterMetrics.length > 0 ? (
                            clusterMetrics.map((metric, index) => (
                              <tr 
                                key={metric.id}
                                style={{
                                  borderTop: index > 0 ? `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}` : undefined,
                                }}
                              >
                                <td className="p-3" style={{ color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary }}>
                                  <div>
                                    <div className="text-sm font-medium">{metric.name}</div>
                                    {metric.definition && (
                                      <div className="text-xs mt-1" style={{ color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary }}>
                                        {metric.definition}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="text-right p-3 text-sm font-medium" style={{ color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary }}>
                                  {metric.type === "income" || metric.type === "expense" || metric.unit === "currency_value"
                                    ? formatEconomicValue(metric.value)
                                    : metric.type === "people_count" 
                                      ? formatNumber(metric.value)
                                      : metric.type === "time_duration"
                                        ? `${metric.value.toFixed(2)} hours`
                                        : formatNumber(metric.value)}
                                </td>
                                <td className="text-center p-3">
                                  <span 
                                    className="px-2 py-1 rounded text-xs font-medium"
                                    style={{
                                      backgroundColor: (metric.type === "income" || metric.type === "expense")
                                        ? theme.colors.status.success + "20"
                                        : metric.type === "people_count" 
                                          ? theme.colors.secondary.main + "20"
                                          : metric.type === "time_duration"
                                            ? "#6F42C1" + "20"
                                            : theme.colors.primary.main + "20",
                                      color: (metric.type === "income" || metric.type === "expense")
                                        ? theme.colors.status.success
                                        : metric.type === "people_count" 
                                          ? theme.colors.secondary.main
                                          : metric.type === "time_duration"
                                            ? "#6F42C1"
                                            : theme.colors.primary.main,
                                    }}
                                  >
                                    {metric.type === "income" ? "Income" 
                                      : metric.type === "expense" ? "Expense"
                                      : metric.type === "people_count" ? "People"
                                      : metric.type === "time_duration" ? "Time"
                                      : metric.type || "Other"}
                                  </span>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} className="p-8 text-center" style={{ color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary }}>
                                No detailed metrics data available for this cluster.
                                <br />
                                <span className="text-sm">
                                  The aggregated values shown above represent the total impact.
                                </span>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div 
              className="flex justify-end p-6 border-t"
              style={{
                borderColor: darkMode ? theme.colors.border.dark : theme.colors.border.light,
              }}
            >
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 rounded-lg font-medium transition-colors"
                style={{
                  backgroundColor: theme.colors.primary.main,
                  color: "white",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImpactDashboard;